import React, { useState } from 'react'
import { BaseView, BaseText } from '../base'
import { AppIcons } from '../AppIcons'
import { Skeleton } from '../Skeleton'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'

interface AvatarProps {
  // Новый API
  imageUrl?: string
  initials?: string
  gradientColors?: [string, string, ...string[]]
  size?: number
  fullSize?: boolean

  // Старый API для совместимости
  uri?: string
  path?: string
  iconName?: string
  iconSize?: number
  name?: string
  containerStyle?: React.CSSProperties
  imageStyle?: React.CSSProperties

  // React Native совместимость
  bgColor?: string | [string, string, ...string[]]
  containerGradientProps?: {
    locations?: number[]
    start?: { x: number; y: number }
    end?: { x: number; y: number }
  }
  isCurrency?: boolean
  skeletonActive?: boolean
}

export const Avatar: React.FC<AvatarProps> = ({
  // Новый API
  imageUrl: newImageUrl,
  initials,
  gradientColors,
  // fullSize = false, // Пока не используется

  // Старый API для совместимости
  uri,
  path,
  size = 48,
  iconName,
  iconSize = 24,
  name,
  containerStyle,
  imageStyle,

  // React Native совместимость
  bgColor,
  containerGradientProps,
  isCurrency = false,
  skeletonActive = false,
}) => {
  const theme = useTheme()
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(false)

  // Определяем источник изображения - приоритет новому API
  const getSource = () => {
    if (newImageUrl) return newImageUrl
    if (uri) return uri
    if (path) return path
    return undefined
  }

  const imageUrl = getSource()

  // Определяем отображаемые инициалы
  const displayInitials = initials || name?.charAt(0)?.toUpperCase() || '?'

  const handleImageLoad = () => {
    setImageLoading(false)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageLoading(false)
    setImageError(true)
  }

  // Определяем стиль фона - градиент или обычный цвет (как в React Native)
  const getBackgroundStyle = () => {
    // Приоритет bgColor из React Native API
    if (bgColor) {
      if (Array.isArray(bgColor)) {
        // Градиент как в React Native
        const startX = containerGradientProps?.start?.x ?? 0.1
        const startY = containerGradientProps?.start?.y ?? 0
        const endX = containerGradientProps?.end?.x ?? 1
        const endY = containerGradientProps?.end?.y ?? 1

        return {
          background: `linear-gradient(${(Math.atan2(endY - startY, endX - startX) * 180) / Math.PI + 90}deg, ${bgColor.join(', ')})`,
        }
      } else {
        // Обычный цвет
        return {
          backgroundColor: bgColor,
        }
      }
    }

    // Fallback на новый API
    if (gradientColors && gradientColors.length >= 2) {
      return {
        background: `linear-gradient(135deg, ${gradientColors.join(', ')})`,
      }
    }

    // Дефолтный цвет
    return {
      backgroundColor: theme.colors.background.textInput,
    }
  }

  // Если активен скелетон, показываем его
  if (skeletonActive) {
    return (
      <Skeleton
        viewBox={`0 0 ${responsiveAdapter(size)} ${responsiveAdapter(size)}`}
        style={{
          width: responsiveAdapter(size),
          height: responsiveAdapter(size),
          position: 'relative',
          ...containerStyle,
        }}
      >
        <circle
          cx={responsiveAdapter(size) / 2}
          cy={responsiveAdapter(size) / 2}
          r={responsiveAdapter(size) / 2}
        />
      </Skeleton>
    )
  }

  // Если есть иконка, показываем её как в RN
  if (iconName) {
    return (
      <BaseView
        style={{
          width: responsiveAdapter(size),
          height: responsiveAdapter(size),
          borderRadius: responsiveAdapter(size) / 2,
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
          ...getBackgroundStyle(),
          ...containerStyle,
        }}
      >
        <AppIcons name={iconName} size={iconSize} color="white" />
      </BaseView>
    )
  }

  // Основной аватар с изображением
  return (
    <BaseView
      style={{
        width: responsiveAdapter(size),
        height: responsiveAdapter(size),
        borderRadius: responsiveAdapter(size) / 2,
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'center',
        ...getBackgroundStyle(),
        ...containerStyle,
      }}
    >
      {imageUrl && !imageError ? (
        <img
          src={imageUrl}
          alt={name || displayInitials || 'Avatar'}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover', // Как в RN: contentFit="cover"
            ...imageStyle,
          }}
        />
      ) : (
        <BaseText
          fontSize={Math.max(12, responsiveAdapter(size) / 2.5)}
          fontFamily="semibold"
          color="label.default"
          style={{
            textAlign: 'center',
            fontWeight: '600',
            color: 'white', // Белый текст на градиентном фоне
          }}
        >
          {displayInitials}
        </BaseText>
      )}

      {/* Loading состояние */}
      {imageLoading && imageUrl && (
        <BaseView
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BaseText fontSize={12} color="label.description">
            ...
          </BaseText>
        </BaseView>
      )}
    </BaseView>
  )
}
